import type { Submission } from "@workshop-survey/shared";
import type { MultiSelectValue } from "../components/questions/types";
import { SURVEY_QUESTIONS, type Question } from "./survey-config";

export type AnswerValue = string | number | MultiSelectValue;
export type AnswerMap = Record<string, AnswerValue>;

export type SurveyState = {
  participant: string;
  submissionId: string;
  startedAt: string;
  index: number;
  direction: 1 | -1;
  answers: AnswerMap;
  status: "idle" | "submitting" | "error" | "submitted";
  errorMessage: string | null;
};

export type SurveyAction =
  | { type: "next" }
  | { type: "back" }
  | { type: "answer"; id: string; value: AnswerValue }
  | { type: "submit_start" }
  | { type: "submit_error"; message: string }
  | { type: "submit_success" };

export function defaultAnswer(q: Question): AnswerValue {
  if (q.type === "slider") return q.defaultValue ?? q.min;
  if (q.type === "multiSelect") return { selected: [], other: "" };
  return "";
}

export function makeInitialState(participant: string): SurveyState {
  const answers: AnswerMap = {};
  for (const q of SURVEY_QUESTIONS) answers[q.id] = defaultAnswer(q);
  return {
    participant,
    submissionId: crypto.randomUUID(),
    startedAt: new Date().toISOString(),
    index: 0,
    direction: 1,
    answers,
    status: "idle",
    errorMessage: null,
  };
}

export function surveyReducer(state: SurveyState, action: SurveyAction): SurveyState {
  switch (action.type) {
    case "next":
      return {
        ...state,
        direction: 1,
        index: Math.min(SURVEY_QUESTIONS.length, state.index + 1),
      };
    case "back":
      return {
        ...state,
        direction: -1,
        index: Math.max(0, state.index - 1),
      };
    case "answer":
      return {
        ...state,
        answers: { ...state.answers, [action.id]: action.value },
      };
    case "submit_start":
      return { ...state, status: "submitting", errorMessage: null };
    case "submit_error":
      return { ...state, status: "error", errorMessage: action.message };
    case "submit_success":
      return { ...state, status: "submitted", errorMessage: null };
  }
}

type Persisted = Pick<
  SurveyState,
  "participant" | "submissionId" | "startedAt" | "index" | "answers"
> & { savedAt: string };

export function toPersisted(state: SurveyState): Persisted {
  return {
    participant: state.participant,
    submissionId: state.submissionId,
    startedAt: state.startedAt,
    index: state.index,
    answers: state.answers,
    savedAt: new Date().toISOString(),
  };
}

export function fromPersisted(p: Persisted): SurveyState {
  return {
    participant: p.participant,
    submissionId: p.submissionId,
    startedAt: p.startedAt,
    index: Math.max(0, Math.min(SURVEY_QUESTIONS.length - 1, p.index)),
    direction: 1,
    answers: p.answers,
    status: "idle",
    errorMessage: null,
  };
}

export function isPersisted(value: unknown): value is Persisted {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.participant === "string" &&
    typeof v.submissionId === "string" &&
    typeof v.startedAt === "string" &&
    typeof v.index === "number" &&
    typeof v.savedAt === "string" &&
    !!v.answers &&
    typeof v.answers === "object"
  );
}

export function buildSubmission(state: SurveyState): Submission {
  const a = state.answers;
  const text = (key: string) => {
    const v = a[key];
    return typeof v === "string" ? v : "";
  };
  const num = (key: string) => {
    const v = a[key];
    return typeof v === "number" ? v : 0;
  };
  const multi = (key: string): MultiSelectValue =>
    (a[key] && typeof a[key] === "object" && "selected" in (a[key] as object)
      ? (a[key] as MultiSelectValue)
      : { selected: [], other: "" });
  const optionalText = (key: string): string | undefined => {
    const t = text(key).trim();
    return t ? t : undefined;
  };
  const otherOf = (key: string): string | undefined => {
    const v = multi(key).other.trim();
    return v ? v : undefined;
  };

  const motivation = multi("motivation");
  const goal = multi("goal_in_six_months");
  const mental = multi("mental_model");
  const dream = multi("dream_project");
  const fears = multi("fears");

  return {
    submissionId: state.submissionId,
    participant: state.participant,
    startedAt: state.startedAt,
    submittedAt: new Date().toISOString(),
    userAgent: navigator.userAgent,
    answers: {
      motivation: motivation.selected,
      motivation_other: otherOf("motivation"),
      goal_in_six_months: goal.selected,
      goal_in_six_months_other: otherOf("goal_in_six_months"),
      mental_model: mental.selected,
      mental_model_other: otherOf("mental_model"),
      ai_experience: text("ai_experience") as Submission["answers"]["ai_experience"],
      ai_experience_story: optionalText("ai_experience_story"),
      dream_project: dream.selected,
      dream_project_other: otherOf("dream_project"),
      fears: fears.selected,
      fears_other: otherOf("fears"),
      terminal_comfort: num("terminal_comfort"),
      operating_system: text("operating_system") as Submission["answers"]["operating_system"],
      anything_else: optionalText("anything_else"),
    },
  };
}
